import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin, Mail, Globe, ChevronRight, Check, Loader2, Linkedin, Instagram } from "lucide-react";
import AdminPage from "./pages/Admin";
import AdminResetPage from "./pages/AdminReset";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tracy's Photos
const PHOTOS = {
  hero: "https://customer-assets.emergentagent.com/job_consulting-tracy/artifacts/t2oidv0p_IMG_8820.JPG",
  aha: "https://customer-assets.emergentagent.com/job_consulting-tracy/artifacts/ub4l7she_IMG_8681.JPG",
  speaker: "https://customer-assets.emergentagent.com/job_consulting-tracy/artifacts/8z1ppo5w_03494E05-41EB-4B58-B391-6663F69DAD4A.JPG",
  award: "https://customer-assets.emergentagent.com/job_consulting-tracy/artifacts/cqhpoj2v_Screenshot%202025-08-03%20at%2010.39.03%E2%80%AFPM.JPG",
  childhood: "https://customer-assets.emergentagent.com/job_consulting-tracy/artifacts/mnd1cvzr_IMG_7540.jpeg",
  selfie: "https://customer-assets.emergentagent.com/job_consulting-tracy/artifacts/0bhxzi7q_IMG_6607.JPG",
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <nav
        data-testid="main-navigation"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "nav-scrolled py-4" : "bg-transparent py-6"
        }`}
      >
        <div className="container mx-auto px-6 lg:px-12 flex items-center justify-between">
          <a href="#hero" className="font-display text-sm tracking-[0.2em]" data-testid="nav-logo">
            TRACY <span className="text-gold-500">PARRIS-BENJAMIN</span>
          </a>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            <button onClick={() => scrollToSection("about")} className="font-display text-xs tracking-[0.15em] text-teal-900 hover:text-gold-500 transition-colors" data-testid="nav-about">
              ABOUT
            </button>
            <button onClick={() => scrollToSection("services")} className="font-display text-xs tracking-[0.15em] text-teal-900 hover:text-gold-500 transition-colors" data-testid="nav-services">
              SERVICES
            </button>
            <button onClick={() => scrollToSection("contact")} className="font-display text-xs tracking-[0.15em] text-teal-900 hover:text-gold-500 transition-colors" data-testid="nav-contact">
              CONTACT
            </button>
            <button
              onClick={() => scrollToSection("contact")}
              className="border border-teal-900 px-6 py-3 font-display text-xs tracking-[0.15em] text-teal-900 hover:bg-teal-900 hover:text-cream-50 transition-all"
              data-testid="nav-cta"
            >
              WORK WITH ME
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden text-teal-900"
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </button>
        </div>
      </nav>

      {/* Mobile Nav Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 right-0 bottom-0 w-72 bg-teal-700 z-50 p-8"
              data-testid="mobile-nav"
            >
              <button onClick={() => setMobileOpen(false)} className="absolute top-6 right-6 text-cream-50">
                <X size={24} />
              </button>
              <div className="mt-16 flex flex-col gap-6">
                <button onClick={() => scrollToSection("about")} className="font-display text-sm tracking-[0.15em] text-cream-50 hover:text-gold-500 text-left">
                  ABOUT
                </button>
                <button onClick={() => scrollToSection("services")} className="font-display text-sm tracking-[0.15em] text-cream-50 hover:text-gold-500 text-left">
                  SERVICES
                </button>
                <button onClick={() => scrollToSection("contact")} className="font-display text-sm tracking-[0.15em] text-cream-50 hover:text-gold-500 text-left">
                  CONTACT
                </button>
                <button
                  onClick={() => scrollToSection("contact")}
                  className="mt-4 bg-gold-500 text-brand-black px-6 py-4 font-display text-xs tracking-[0.15em]"
                >
                  WORK WITH ME
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" className="min-h-screen bg-cream-50 relative pt-20" data-testid="hero-section">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-5rem)]">
        {/* Left - Photo */}
        <div className="relative h-[60vh] lg:h-auto order-1 lg:order-1">
          <div className="gold-accent-bar" />
          <img
            src={PHOTOS.hero}
            alt="Tracy Parris-Benjamin"
            className="w-full h-full object-cover object-[center_20%]"
            data-testid="hero-image"
          />
          {/* Subtle gradient fade to cream on right edge only */}
          <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-cream-100 to-transparent hidden lg:block" />
        </div>

        {/* Right - Content */}
        <motion.div
          className="flex flex-col justify-center px-8 lg:px-16 py-16 lg:py-0 order-2 lg:order-2 bg-cream-100"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6">
            <div className="w-8 h-[1px] bg-gold-500" />
            <span className="font-display text-xs tracking-[0.2em] text-gold-500">THE PARRIS CONSULTING GROUP</span>
          </motion.div>

          <motion.p variants={fadeInUp} className="font-heading text-xl italic text-teal-700 mb-4">
            Hello, I'm Tracy.
          </motion.p>

          {/* Role/Title Subtitle */}
          <motion.div 
            variants={fadeInUp}
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6"
          >
            <p className="font-body text-xs tracking-[0.12em] uppercase text-[#9A9590] mb-1">
              Managing Partner · Entrepreneur · Motivation Speaker · Coach
            </p>
            <p className="font-body text-xs tracking-[0.12em] uppercase text-teal-700 mb-4">
              Leading with Empathy and Humility · Purpose-Driven Leadership
            </p>
            <div className="w-10 h-[1px] bg-gold-500" />
          </motion.div>

          <motion.h1 variants={fadeInUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-brand-black mb-8">
            I help people<br />
            and organizations<br />
            find their <span className="italic text-teal-500">why</span> —<br />
            then <span className="text-gold-500">move.</span>
          </motion.h1>

          <motion.p variants={fadeInUp} className="font-body text-lg text-teal-900/80 leading-relaxed mb-8 max-w-lg">
            I'm a connector, an advocate, and a strategist. Whether I'm coaching a leader, facilitating a room, or building a roadmap for an organization — I show up fully, ask the hard questions, and stay until the work is done.
          </motion.p>

          <motion.div variants={fadeInUp} className="flex flex-wrap gap-4 mb-10">
            <button onClick={() => scrollToSection("contact")} className="btn-gold" data-testid="hero-cta-primary">
              LET'S WORK TOGETHER
            </button>
            <button onClick={() => scrollToSection("about")} className="btn-ghost flex items-center gap-2" data-testid="hero-cta-secondary">
              MY STORY <ChevronRight size={16} />
            </button>
          </motion.div>

          <motion.div variants={fadeInUp} className="border-l-2 border-gold-500/30 pl-6 max-w-md">
            <p className="font-heading text-lg italic text-teal-900/70 mb-2">
              "The work I do is deeply personal. I'm not just a consultant — I'm invested in your growth."
            </p>
            <p className="font-display text-xs tracking-[0.15em] text-teal-900/50">— TRACY PARRIS-BENJAMIN</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Values Ticker Strip
const ValuesTicker = () => {
  const values = ["Coaching", "Facilitation", "Consulting", "Advocacy", "Women's Health", "Health Equity", "Community"];
  
  return (
    <section className="bg-teal-700 py-5 overflow-hidden" data-testid="values-ticker">
      <div className="ticker-wrapper">
        <div className="ticker-content">
          {[...values, ...values, ...values].map((value, idx) => (
            <span key={idx} className="flex items-center">
              <span className="font-display text-sm tracking-[0.2em] text-gold-500 uppercase px-6">{value}</span>
              <span className="w-2 h-2 rotate-45 bg-gold-500/60" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const credentials = [
    "LMSW — Licensed Master Social Worker",
    "Fellow, NJ Healthcare Executive Leadership Academy",
    "Fmr. President, American Heart Association — Northern NJ",
    "Senior Director, Health Equity — Horizon BCBS of NJ",
    "Diversity MBA Top Leader Under 50"
  ];

  const causes = ["Women's Health", "Health Equity", "Advocacy", "Multi-Gen Audiences", "Community", "Menstrual Health"];

  return (
    <section id="about" className="bg-cream-50 py-20 lg:py-32" data-testid="about-section">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Content */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[1px] bg-gold-500" />
              <span className="font-display text-xs tracking-[0.2em] text-gold-500">ABOUT TRACY</span>
            </motion.div>

            <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-black mb-8 leading-tight">
              "This season is about <span className="italic text-teal-500">showing up</span> as exactly who I am."
            </motion.h2>

            <motion.div variants={fadeInUp} className="space-y-6 text-teal-900/80 font-body leading-relaxed mb-8">
              <p>
                I spent over a decade leading health equity initiatives, coaching teams, and advising organizations through complex change. With over 25 years of experience in the non-profit sector, extensive expertise in counseling women and children, supporting survivors of domestic violence, and leading support groups and parenting workshops — I've sat in boardrooms and community centers. I've worked with executives and with middle schoolers. What I've learned is that the work — no matter the setting — is always about people.
              </p>
              <p>
                The Parris Consulting Group is my next chapter. It's where my experience in healthcare, advocacy, and leadership development comes together under one roof — built around a simple conviction: when people know their why, extraordinary things become possible.
              </p>
              <p>
                I work across generations, across industries, and across every kind of table. I bring the same energy whether I'm on a stage moderating a panel or in a one-on-one coaching session. This is personal work — and I take it personally.
              </p>
            </motion.div>

            {/* Pull Quote */}
            <motion.div 
              variants={fadeInUp}
              className="border-l-[3px] border-gold-500 pl-6 py-5 bg-[rgba(26,122,110,0.05)] my-8"
            >
              <p className="font-heading text-2xl italic text-teal-700 leading-relaxed mb-3">
                "Leading with empathy and kindness as a Superpower."
              </p>
              <p className="font-body text-[11px] tracking-[0.14em] text-[#9A9590]">
                — TRACY PARRIS-BENJAMIN
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="border-t border-gold-700/20 pt-8 mb-8">
              <div className="space-y-3">
                {credentials.map((cred, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-teal-500 mt-2 flex-shrink-0" />
                    <span className="font-body text-sm text-teal-900">{cred}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-2">
              {causes.map((cause, idx) => (
                <span key={idx} className="cause-tag">{cause}</span>
              ))}
            </motion.div>
          </motion.div>

          {/* Right - Stats */}
          <motion.div
            className="space-y-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="stat-card p-8">
              <span className="font-heading text-5xl text-teal-900">25+</span>
              <p className="font-body text-teal-900/70 mt-2">Years bridging healthcare, advocacy, and organizational leadership</p>
            </motion.div>

            <motion.div variants={fadeInUp} className="stat-card gold p-8">
              <span className="font-heading text-5xl text-gold-500">9,000+</span>
              <p className="font-body text-teal-900/70 mt-2">Members served through the Neighbors in Health initiative at Horizon BCBS NJ</p>
            </motion.div>

            <motion.div variants={fadeInUp} className="stat-card p-8">
              <span className="font-heading text-3xl text-teal-900">Multi-Industry</span>
              <p className="font-body text-teal-900/70 mt-2">Healthcare · Education · Nonprofits · Corporate · Community</p>
            </motion.div>

            {/* Background Card */}
            <motion.div variants={fadeInUp} className="stat-card p-8">
              <span className="font-heading text-3xl text-teal-900">Background</span>
              <p className="font-body text-teal-900/70 mt-2">Hospital discharge planning and quality improvement within the payer space</p>
            </motion.div>

            {/* Experience Card */}
            <motion.div variants={fadeInUp} className="stat-card p-8">
              <span className="font-heading text-3xl text-teal-900">Experience</span>
              <p className="font-body text-teal-900/70 mt-2">Experience in the non profit space, counseling women and children, survivors of domestic violence, and leading support groups</p>
            </motion.div>

            <motion.div variants={fadeInUp} className="stat-card quote p-8">
              <p className="font-heading text-xl italic text-teal-700 leading-relaxed">
                "I believe every person and every organization has a why. My job is to help you find it — and build from there."
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      title: "Consulting",
      description: "Strategic guidance for organizations navigating change, growth, or realignment. Tracy partners with leaders to build clear, actionable roadmaps — grounded in your mission and designed to actually get you where you're going.",
      audience: "Organizations & Leadership Teams"
    },
    {
      title: "Coaching",
      description: "Transformative one-on-one and group coaching for leaders at every stage. Whether you're stepping into a new role, navigating a critical transition, or simply ready to lead better — Tracy helps you get clear, get unstuck, and get moving.",
      audience: "Individuals & Teams"
    },
    {
      title: "Speaking & Moderating",
      description: "Engaging keynotes, panel moderation, and facilitated conversations that challenge thinking and move rooms. Tracy brings the kind of presence and depth that makes people lean in — and leave with something they can use.",
      audience: "Groups & Organizations"
    }
  ];

  const scrollToContact = () => {
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="services" className="bg-teal-700 py-20 lg:py-32 relative" data-testid="services-section">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: `linear-gradient(to right, #C9A84C 1px, transparent 1px), linear-gradient(to bottom, #C9A84C 1px, transparent 1px)`,
        backgroundSize: '60px 60px'
      }} />

      <div className="container mx-auto px-6 lg:px-12 relative">
        {/* Header */}
        <motion.div
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-16"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <div>
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-4">
              <div className="w-8 h-[1px] bg-gold-500" />
              <span className="font-display text-xs tracking-[0.2em] text-gold-500">WHAT I OFFER</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl text-cream-50">
              Three ways to work with Tracy.
            </motion.h2>
          </div>
          <motion.button
            variants={fadeInUp}
            onClick={scrollToContact}
            className="mt-6 lg:mt-0 btn-gold flex items-center gap-2"
            data-testid="services-cta"
          >
            LET'S TALK <ChevronRight size={16} />
          </motion.button>
        </motion.div>

        {/* Service Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              variants={fadeInUp}
              className="service-card bg-cream-50 p-8 hover:-translate-y-2 transition-transform duration-500"
              data-testid={`service-card-${idx}`}
            >
              <div className="w-8 h-8 rotate-45 bg-gold-500 mb-6" />
              <h3 className="font-heading text-2xl text-teal-900 mb-4">{service.title}</h3>
              <p className="font-body text-teal-900/70 leading-relaxed mb-6">{service.description}</p>
              <span className="font-display text-xs tracking-[0.15em] text-teal-500 uppercase">
                For: {service.audience}
              </span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Photo Strip Section
const PhotoStripSection = () => {
  const photos = [
    { src: PHOTOS.aha, alt: "Tracy speaking at American Heart Association event" },
    { src: PHOTOS.speaker, alt: "Tracy at NJ Health Care Quality Institute conference" },
    { src: PHOTOS.award, alt: "Tracy at Diversity MBA Top 100 Under 50 awards" },
    { src: PHOTOS.childhood, alt: "Tracy as a young child" },
    { src: PHOTOS.selfie, alt: "Tracy casual portrait" }
  ];

  return (
    <section className="photo-strip-container" data-testid="photo-strip">
      {photos.map((photo, idx) => (
        <div key={idx} className="photo-strip-item" data-testid={`photo-strip-item-${idx}`}>
          <img src={photo.src} alt={photo.alt} />
        </div>
      ))}
    </section>
  );
};

// Industries Section
const IndustriesSection = () => {
  const industries = [
    "Healthcare", "Education / Schools", "Nonprofits", "Corporate",
    "Community Organizations", "Government", "Philanthropy", "Faith-Based",
    "Women's Groups", "Youth & Young Adults"
  ];

  return (
    <section className="bg-teal-700 py-20 lg:py-24" data-testid="industries-section">
      <div className="container mx-auto px-6 lg:px-12 text-center">
        <motion.h2
          className="font-heading text-3xl sm:text-4xl lg:text-5xl text-cream-50 italic mb-4"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          I work across every kind of table.
        </motion.h2>
        <motion.p
          className="font-body text-cream-50/60 mb-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Industries & audiences I've served
        </motion.p>

        <motion.div
          className="flex flex-wrap justify-center gap-3"
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          {industries.map((industry, idx) => (
            <motion.span
              key={idx}
              variants={fadeInUp}
              className="industry-tag"
              data-testid={`industry-tag-${idx}`}
            >
              {industry}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Intake Form Component
const IntakeForm = () => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    organization: "",
    support_type: "",
    engagement_for: "",
    timeline: "",
    situation_description: "",
    referral_source: ""
  });

  const supportTypes = [
    "Consulting",
    "Coaching (Individual)",
    "Coaching (Team or Group)",
    "Speaking / Keynote",
    "Panel Moderation or Facilitation",
    "Not sure yet — let's talk"
  ];

  const engagementFor = [
    "Myself (individual)",
    "My leadership team",
    "My entire organization",
    "A specific department or group"
  ];

  const timelines = [
    "Immediately",
    "Within the next 30 days",
    "Within the next 90 days",
    "Still exploring options"
  ];

  const referralSources = [
    "Personal referral",
    "LinkedIn",
    "Google search",
    "Social media",
    "Other"
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    setIsThinking(true);
    setTimeout(() => {
      setIsThinking(false);
      setStep(step + 1);
    }, 800);
  };

  const prevStep = () => setStep(step - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setIsThinking(true);

    try {
      await axios.post(`${API}/intake`, formData);
      setTimeout(() => {
        setIsThinking(false);
        setIsSuccess(true);
      }, 1500);
    } catch (error) {
      console.error("Error submitting form:", error);
      setIsThinking(false);
      setIsSubmitting(false);
    }
  };

  const canProceedStep1 = formData.first_name && formData.last_name && formData.email;
  const canProceedStep2 = formData.support_type && formData.engagement_for;
  const canSubmit = formData.situation_description;

  if (isSuccess) {
    return (
      <div data-testid="form-success">
        <div className="bg-teal-900 p-4 flex items-center justify-between">
          <span className="font-display text-xs tracking-[0.15em] text-cream-50">Start a Conversation</span>
          <span className="pulse-badge bg-gold-500 text-brand-black font-display text-[10px] tracking-[0.1em] px-3 py-1">
            AI-ASSISTED INTAKE
          </span>
        </div>
        <motion.div
          className="text-center py-12 px-8"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <div className="w-20 h-20 mx-auto mb-6 bg-teal-500 rotate-45 flex items-center justify-center">
            <Check className="-rotate-45 text-cream-50" size={40} />
          </div>
          <h3 className="font-heading text-2xl text-teal-900 mb-4">Thank you!</h3>
          <p className="font-body text-teal-900/70 max-w-sm mx-auto">
            Tracy will review your inquiry and be in touch within 1–2 business days to schedule your consultation. We look forward to the conversation.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="intake-form" data-testid="intake-form">
      {/* Header */}
      <div className="bg-teal-900 p-4 flex items-center justify-between">
        <span className="font-display text-xs tracking-[0.15em] text-cream-50">Start a Conversation</span>
        <span className="pulse-badge bg-gold-500 text-brand-black font-display text-[10px] tracking-[0.1em] px-3 py-1">
          AI-ASSISTED INTAKE
        </span>
      </div>

      <div className="p-8">
      {/* Step Progress */}
      <div className="flex justify-center gap-3 mb-8">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`step-dot ${s === step ? 'active' : ''} ${s < step ? 'completed' : ''}`}
          />
        ))}
      </div>

      {/* Thinking Animation */}
      <AnimatePresence>
        {isThinking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-8"
          >
            <div className="flex items-center justify-center gap-1 mb-2">
              <span className="thinking-dot w-2 h-2 bg-teal-500 rounded-full" />
              <span className="thinking-dot w-2 h-2 bg-teal-500 rounded-full" />
              <span className="thinking-dot w-2 h-2 bg-teal-500 rounded-full" />
            </div>
            <span className="font-body text-sm text-teal-900/60">Reviewing your responses...</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Step Content */}
      {!isThinking && (
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <p className="font-body text-sm text-teal-900/60 mb-6">A few quick questions so Tracy can come to your first conversation fully prepared.</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Your first name"
                    required
                    className="w-full"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Your last name"
                    required
                    className="w-full"
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div>
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                  className="w-full"
                  data-testid="input-email"
                />
              </div>

              <div>
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">Organization / Company</label>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="Where do you work or lead?"
                  className="w-full"
                  data-testid="input-organization"
                />
              </div>

              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceedStep1}
                className="w-full btn-teal disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="btn-step1-next"
              >
                CONTINUE <ChevronRight size={16} />
              </button>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div>
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">What type of support are you looking for? *</label>
                <select
                  name="support_type"
                  value={formData.support_type}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-teal-900/20 py-3 outline-none"
                  data-testid="select-support-type"
                >
                  <option value="">Select an option</option>
                  {supportTypes.map((type, idx) => (
                    <option key={idx} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">Who is this engagement for? *</label>
                <select
                  name="engagement_for"
                  value={formData.engagement_for}
                  onChange={handleChange}
                  required
                  className="w-full bg-transparent border-b border-teal-900/20 py-3 outline-none"
                  data-testid="select-engagement-for"
                >
                  <option value="">Select an option</option>
                  {engagementFor.map((option, idx) => (
                    <option key={idx} value={option}>{option}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">When are you looking to get started?</label>
                <select
                  name="timeline"
                  value={formData.timeline}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-teal-900/20 py-3 outline-none"
                  data-testid="select-timeline"
                >
                  <option value="">Select an option</option>
                  {timelines.map((t, idx) => (
                    <option key={idx} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 btn-ghost"
                  data-testid="btn-step2-back"
                >
                  BACK
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!canProceedStep2}
                  className="flex-1 btn-teal disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="btn-step2-next"
                >
                  CONTINUE <ChevronRight size={16} />
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div>
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">Briefly describe your situation or challenge *</label>
                <textarea
                  name="situation_description"
                  value={formData.situation_description}
                  onChange={handleChange}
                  placeholder="What's going on, and what would you like to see change?"
                  required
                  rows={4}
                  className="w-full resize-none"
                  data-testid="textarea-situation"
                />
              </div>

              <div>
                <label className="font-display text-xs tracking-[0.1em] text-teal-900/60 uppercase block mb-2">How did you hear about Tracy?</label>
                <select
                  name="referral_source"
                  value={formData.referral_source}
                  onChange={handleChange}
                  className="w-full bg-transparent border-b border-teal-900/20 py-3 outline-none"
                  data-testid="select-referral"
                >
                  <option value="">Select an option</option>
                  {referralSources.map((source, idx) => (
                    <option key={idx} value={source}>{source}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={prevStep}
                  className="flex-1 btn-ghost"
                  data-testid="btn-step3-back"
                >
                  BACK
                </button>
                <button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="flex-1 btn-gold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="btn-submit"
                >
                  {isSubmitting ? <Loader2 className="animate-spin" size={16} /> : "SUBMIT"}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}
      </div>
    </form>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="bg-cream-100 py-20 lg:py-32" data-testid="contact-section">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Content */}
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[1px] bg-gold-500" />
              <span className="font-display text-xs tracking-[0.2em] text-gold-500">WORK WITH ME</span>
            </motion.div>

            <motion.h2 variants={fadeInUp} className="font-heading text-3xl sm:text-4xl lg:text-5xl text-brand-black mb-6 leading-tight">
              Every great outcome starts with a <span className="italic text-teal-500">real conversation.</span>
            </motion.h2>

            <motion.p variants={fadeInUp} className="font-body text-lg text-teal-900/80 leading-relaxed mb-10">
              Whether you're looking for a strategic thought partner, a coach to help you level up, or a speaker who can hold a room — it all starts here. Share a little about where you are and what you need, and Tracy will be in touch.
            </motion.p>

            <motion.div variants={fadeInUp} className="space-y-4">
              <div className="flex items-center gap-4">
                <MapPin className="text-gold-500" size={20} />
                <span className="font-body text-teal-900">Serving clients nationwide</span>
              </div>
              <div className="flex items-center gap-4">
                <Mail className="text-gold-500" size={20} />
                <a href="mailto:tracy@theparrisconsultinggroup.com" className="font-body text-teal-900 hover:text-teal-500 transition-colors">
                  tracy@theparrisconsultinggroup.com
                </a>
              </div>
              <div className="flex items-center gap-4">
                <Globe className="text-gold-500" size={20} />
                <span className="font-body text-teal-900">theparrisconsultinggroup.com</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            className="bg-cream-50 shadow-xl"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <IntakeForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer className="bg-brand-black py-16" data-testid="footer">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12 mb-12">
          {/* Brand */}
          <div>
            <h4 className="font-display text-lg tracking-[0.1em] text-cream-50 mb-2">
              THE <span className="text-gold-500">PARRIS</span> CONSULTING <span className="text-gold-500">GROUP</span>
            </h4>
            <p className="font-heading text-lg italic text-cream-50/60">Strategy · Vision · Results</p>
          </div>

          {/* Navigate */}
          <div>
            <h5 className="font-display text-xs tracking-[0.2em] text-gold-500 mb-4">NAVIGATE</h5>
            <div className="space-y-3">
              <button onClick={() => scrollToSection("about")} className="block font-body text-cream-50/70 hover:text-cream-50 transition-colors">
                About Tracy
              </button>
              <button onClick={() => scrollToSection("services")} className="block font-body text-cream-50/70 hover:text-cream-50 transition-colors">
                Services
              </button>
              <button onClick={() => scrollToSection("contact")} className="block font-body text-cream-50/70 hover:text-cream-50 transition-colors">
                Work With Me
              </button>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h5 className="font-display text-xs tracking-[0.2em] text-gold-500 mb-4">CONTACT</h5>
            <div className="space-y-3">
              <a href="mailto:tracy@theparrisconsultinggroup.com" className="block font-body text-sm text-cream-50/70 hover:text-cream-50 transition-colors break-all">
                tracy@theparrisconsultinggroup.com
              </a>
              <span className="block font-body text-sm text-cream-50/70 break-all">
                theparrisconsultinggroup.com
              </span>
            </div>
          </div>

          {/* Connect */}
          <div>
            <h5 className="font-display text-xs tracking-[0.2em] text-gold-500 mb-4">CONNECT</h5>
            <div className="space-y-3">
              <a 
                href="https://www.linkedin.com/in/tracy-parris-benjamin"
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 font-body text-cream-50/70 hover:text-gold-500 transition-colors"
                data-testid="linkedin-link"
              >
                <Linkedin size={18} className="flex-shrink-0" /> <span>LinkedIn</span>
              </a>
              <a 
                href="https://www.instagram.com/sugarplumpartiesgreenbrook"
                target="_blank" 
                rel="noopener noreferrer" 
                className="flex items-center gap-2 font-body text-cream-50/70 hover:text-gold-500 transition-colors"
                data-testid="instagram-link"
              >
                <Instagram size={18} className="flex-shrink-0" /> <span>Instagram</span>
              </a>
              
              {/* Also Visit Separator */}
              <div className="border-t border-cream-50/10 mt-4 pt-4">
                <span className="font-display text-[9px] tracking-[0.16em] text-gold-500 uppercase block mb-2">ALSO VISIT</span>
                <a 
                  href="https://sugarplumpartiesnj.com/"
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="font-body text-xs text-cream-50/50 hover:text-cream-50/70 transition-colors"
                >
                  Sugar Plum Parties NJ
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-cream-50/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-body text-sm text-cream-50/50">
            © 2026 The Parris Consulting Group · All Rights Reserved
          </p>
          <div className="flex items-center gap-4">
            <a
              href="/admin"
              className="font-body text-xs text-cream-50/30 hover:text-cream-50/70 transition-colors"
              data-testid="footer-admin-link"
            >
              Admin
            </a>
            <span className="text-cream-50/20 text-xs">·</span>
            <p className="font-body text-sm text-cream-50/50">
              Site by <a href="https://jditechinnovations.com" target="_blank" rel="noopener noreferrer" className="text-gold-500 hover:text-gold-700 transition-colors">JDI Tech Innovations</a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Home Page Component
const HomePage = () => {
  return (
    <>
      <Navigation />
      <HeroSection />
      <ValuesTicker />
      <AboutSection />
      <ServicesSection />
      <PhotoStripSection />
      <IndustriesSection />
      <ContactSection />
      <Footer />
    </>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-cream-50">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/reset" element={<AdminResetPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
